import { html, svg, type TemplateResult } from "lit";
import { fpo } from "../../lib/fpo";

import "./hero.scss";

const TITLE = "Enter the race against blood cancer";

const BODY =
  "Please watch the video to learn more about Palmer’s incredible story. You can donate below to support kids and families like Palmer at the link below or by texting Give to 39-999.";

export interface HeroProps {
  /** Video poster. Figma 421:11 — 820 x 461.5 desktop, 379.4 x 213.5 mobile. */
  readonly poster: string;
  /** Optional link for the video. When omitted the play button emits an event. */
  readonly videoHref: string;
  /** Self-hosted video file. Takes precedence over every other variant. */
  readonly videoSrc: string;
  /**
   * Whether the self-hosted video has ever played. Lifted to the root element
   * (CLAUDE.md) because it decides two things at once: our button covers the
   * whole poster until it is true, and the browser's own controls only appear
   * after it — showing both at once puts two play affordances on one poster.
   */
  readonly videoStarted: boolean;
  /** Whether it is paused right now, which picks the glyph on our button. */
  readonly videoPaused: boolean;
  /**
   * Called from the video's own `play` / `pause` events, not from our click, so
   * the state follows the element however it was driven — our own button, the
   * browser's controls, the keyboard, or the end of the video.
   */
  readonly onVideoPlaying: (playing: boolean) => void;
  /** Embedded player URL. Takes precedence over `poster` / `videoHref`. */
  readonly videoEmbed: string;
  readonly ctaHref: string;
  /** Element the `valvoline-hero:play` event is dispatched from. */
  readonly host: HTMLElement;
}

/**
 * Gradient hero. Figma desktop 421:9 (652px band, copy left / video right);
 * mobile 428:957 + 428:960 (copy, then a CTA band, then the video).
 */
export function renderHero(props: HeroProps): TemplateResult {
  return html`
    <section class="valvoline-hero">
      <div class="valvoline-hero__inner">
        <div class="valvoline-hero__copy">
          <h1 class="valvoline-hero__title">${TITLE}</h1>
          <p class="valvoline-hero__body">${BODY}</p>
          <a class="valvoline-hero__cta" href=${props.ctaHref} target="_blank" rel="noopener"
            >Donate</a
          >
        </div>

        <div class="valvoline-hero__media">${renderVideo(props)}</div>
      </div>
    </section>
  `;
}

function renderVideo(props: HeroProps): TemplateResult {
  const { poster, videoHref, videoSrc, videoEmbed, videoStarted, videoPaused, host } = props;

  // The file we serve ourselves, and the default.
  //
  // It keeps the Figma play button (`482:33`) rather than handing the whole box
  // to the browser. Three states, in one element:
  //
  //   - before the first play: the poster, our button over the whole box, and NO
  //     native controls, because there is nothing else to click yet;
  //   - playing: native controls on (seek, volume, fullscreen — the platform
  //     draws those correctly everywhere and we are not rebuilding them), plus
  //     our own button, which fades in on hover as a pause;
  //   - paused after having played: the same button, showing play again.
  //
  // The button stays centred throughout, but after the first play it shrinks to
  // the glyph alone. It MUST NOT keep covering the whole box: an overlay across
  // the video eats the clicks meant for the controls bar underneath, so seeking
  // would break.
  //
  // `preload="metadata"` is load-bearing, not a nicety: left to its default of
  // "auto" Chrome pulls the whole 17 MB on page load, for every visitor,
  // whether or not anyone presses play. "none" would go too far the other way —
  // it costs the poster nothing (that attribute is a still, not the file) but it
  // also leaves the duration unknown, and `video_progress` is a percentage of it.
  if (videoSrc) {
    return html`<div class="valvoline-hero__stage">
      <video
        class="valvoline-hero__player"
        src=${videoSrc}
        poster=${poster || fpo(820, 462, "Hendrick Motorsports")}
        title="Palmer Goforth designing the No. 24 paint scheme"
        ?controls=${videoStarted}
        playsinline
        preload="metadata"
        @play=${() => props.onVideoPlaying(true)}
        @pause=${() => props.onVideoPlaying(false)}
      ></video>
      <button
        class=${videoStarted ? "valvoline-hero__toggle" : "valvoline-hero__start"}
        type="button"
        aria-label=${!videoStarted || videoPaused ? "Play the video" : "Pause the video"}
        @click=${toggleVideo}
      >
        ${!videoStarted || videoPaused ? playIcon() : pauseIcon()}
      </button>
    </div>`;
  }

  // An embedded player replaces the poster outright — the vendor draws its own
  // play control, so ours would sit on top of theirs, and there is nothing left
  // for `valvoline-hero:play` to mean: the click never reaches this document.
  if (videoEmbed) {
    return html`<iframe
      class="valvoline-hero__embed"
      src=${videoEmbed}
      title="Palmer Goforth designing the No. 24 paint scheme"
      allow="fullscreen; picture-in-picture; encrypted-media"
      allowfullscreen
      loading="lazy"
    ></iframe>`;
  }

  const figure = html`
    <img
      class="valvoline-hero__poster"
      src=${poster || fpo(820, 462, "Hendrick Motorsports")}
      alt="Palmer Goforth with the Hendrick Motorsports team"
    />
    ${playIcon()}
  `;

  return videoHref
    ? html`<a
        class="valvoline-hero__video"
        href=${videoHref}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Play the video"
        >${figure}</a
      >`
    : html`<button
        class="valvoline-hero__video"
        type="button"
        aria-label="Play the video"
        @click=${() => play(host)}
      >
        ${figure}
      </button>`;
}

// NOTE: placeholder glyphs. The real play button is Figma vector 421:12
// (52.5 x 52.5) and still needs exporting — swap these for the asset. Figma has
// no pause state at all, so that one has nothing to swap for and is ours.
//
// Both are the same disc at the same size, so the button does not jump or resize
// when the state flips; only what is inside the circle changes.
function playIcon(): TemplateResult {
  return glyph(svg`<path d="M20 15.5 L38 26 L20 36.5 Z" fill="#00316d" />`);
}

function pauseIcon(): TemplateResult {
  return glyph(svg`
    <rect x="19" y="16" width="5.5" height="20" rx="1" fill="#00316d" />
    <rect x="27.5" y="16" width="5.5" height="20" rx="1" fill="#00316d" />
  `);
}

/**
 * The disc, with whatever mark goes inside it.
 *
 * The mark MUST be built with Lit's `svg` tag, not `html`. Lit parses each
 * template on its own, so a `<path>` or `<rect>` in an `html` template is parsed
 * in HTML context, where those are unknown elements: they come out as HTML nodes
 * of zero size, self-closing is ignored so siblings nest inside each other, and
 * the result is a disc with nothing on it. It fails silently — no error, no
 * warning, just a blank circle.
 */
function glyph(mark: TemplateResult): TemplateResult {
  return html`
    <span class="valvoline-hero__play" aria-hidden="true">
      <svg viewBox="0 0 52 52" width="52" height="52" focusable="false">
        <circle cx="26" cy="26" r="25" fill="rgba(255,255,255,0.92)" />
        ${mark}
      </svg>
    </span>
  `;
}

function play(host: HTMLElement): void {
  host.dispatchEvent(new CustomEvent("valvoline-hero:play", { bubbles: true, composed: true }));
}

/**
 * Play or pause the self-hosted video from our own button.
 *
 * The element is found from the click rather than held in a ref: this template
 * re-renders whenever the play state changes, so anything captured up front
 * would be reading a stale tree.
 *
 * The state is NOT set here — the video's own `play`/`pause` events do that (see
 * the bindings in `renderVideo`). Setting it here as well would be a second
 * source of truth for the same fact, and it would be the wrong one the moment
 * playback is driven from the browser's controls instead of this button.
 *
 * It deliberately does not dispatch `valvoline-hero:play` either. That event
 * means "the page has no player, open your own", and it is what reports
 * `video_start` for the poster variant — firing it here would report one view as
 * two starts, once as `poster` and once as `self`. The real `video_start` comes
 * from the `playing` event on the element (see analytics-events.ts).
 *
 * `play()` returns a promise that rejects if the browser refuses. It cannot here
 * — this is inside a click, so the gesture requirement is met — but an unhandled
 * rejection would still surface in the console, so it is swallowed.
 */
function toggleVideo(event: MouseEvent): void {
  const stage = (event.currentTarget as Element).closest(".valvoline-hero__stage");
  const video = stage?.querySelector("video");
  if (!video) return;
  if (video.paused) video.play().catch(() => {});
  else video.pause();
}
